// dnsClient.js — the seam onto the cluster's DNS anchor, spoken directly on its own origin.
//
// The DNS anchor is never a leaf: it serves exactly one cluster-wide capability, so this browser
// always reaches it with the CLUSTER session (`clusterSession`), the same one every other cluster
// surface already holds, and mints or renews nothing of its own — the anchor verifies that session
// itself (kgsm-dns's `ClusterBearer`).
//
// The origin is resolved through the capability ASSIGNMENT (`clusterStore.holderOf`) rather than
// through whichever member's page happens to be open. A card pinned to the dashboard has no page
// above it naming a member, and every dns.* widget is keyed on the capability rather than a member id
// so a pin follows a failover instead of pointing at a member that has stopped answering for it.

import { authorized } from "./authorizedFetch.js";
import { clusterSession } from "./sessionStore.js";
import { clusterStore } from "./stores/cluster.js";

const CAPABILITY = "dns";

// Built fresh per call, exactly like `anchor.js`'s own calls — `clusterSession`'s two functions
// close over the live session, so nothing here is cached that could go stale, and no import-order
// assumption is made about when the session is first read.
const http = () => authorized(clusterSession);

function originOf() {
  const holder = clusterStore.holderOf(CAPABILITY);
  if (!holder) return null;
  const member = clusterStore.getState().nodes.find((n) => n.nodeId === holder);
  const url = member && member.clientUrl;
  return url ? url.replace(/\/+$/, "") : null;
}

// No member currently answers for the capability — the honest reason every call below refuses with
// when the assignment names nobody or the roster has no address for whoever holds it.
function noRoute() {
  const err = new Error("No member currently holds this cluster's dns capability.");
  err.code = "no_route";
  err.noRoute = true;
  err.userMessage = err.message;
  return err;
}

// One shape for every refusal the anchor itself answers with, so a caller reads `.userMessage`
// without keeping a second copy of what each status code means.
function refusal(res) {
  if (res.unreachable) {
    const err = new Error("Couldn’t reach the dns anchor.");
    err.status = 0; err.code = "unreachable"; err.userMessage = err.message;
    return err;
  }
  if (res.unauthenticated) {
    const err = new Error("This session couldn’t be renewed — sign in again to continue.");
    err.status = 401; err.code = "unauthenticated"; err.userMessage = err.message;
    return err;
  }
  const body = res.body;
  const message = (body && body.message) || ("HTTP " + res.status);
  const err = new Error(message);
  err.status = res.status;
  err.code = (body && body.error) || null;
  err.userMessage = message;
  return err;
}

// Reads. Replayable — a refusal means the anchor never handled the request, so a renewal and a
// second send is the first time it runs rather than a second one.
async function dnsStatus() {
  const base = originOf();
  if (!base) throw noRoute();
  const res = await http().json(base + "/api/v1/cluster/dns", { headers: { Accept: "application/json" } });
  if (!res.ok) throw refusal(res);
  return res.body;
}

async function dnsEvents(limit) {
  const base = originOf();
  if (!base) throw noRoute();
  const qs = limit ? "?limit=" + encodeURIComponent(limit) : "";
  const res = await http().json(base + "/api/v1/cluster/dns/events" + qs, { headers: { Accept: "application/json" } });
  if (!res.ok) throw refusal(res);
  return (res.body && res.body.events) || [];
}

// Writes. `once` — none of these may be sent twice: a zone check and a certificate renewal both ask
// something outside this browser to act, and a certificate order is rate-limited by the issuer, so a
// refusal here is reported as it stands rather than replayed after a silent token renewal.
// Each write, as the method and path it is sent with — named once, so the page asking whether one may
// be made (`persona.callRefusal("dns", …)`) asks about exactly the request that is sent.
const DNS_WRITES = {
  zoneCheck: () => ["POST", "/api/v1/cluster/dns/zone/check"],
  renew: (name) => ["POST", "/api/v1/cluster/dns/certificates/" + encodeURIComponent(name || "_") + "/renew"],
  addAlias: () => ["POST", "/api/v1/cluster/dns/aliases"],
  removeAlias: (name) => ["DELETE", "/api/v1/cluster/dns/aliases/" + encodeURIComponent(name || "_")],
};

async function send([method, path], body) {
  const base = originOf();
  if (!base) throw noRoute();
  const res = await http().once(base + path, body === undefined
    ? { method, headers: { Accept: "application/json" } }
    : { method, headers: { Accept: "application/json", "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (!res.ok) throw refusal(res);
  return res.body;
}

const checkZoneNow = () => send(DNS_WRITES.zoneCheck()).then(() => {});
const renewCertificate = (name) => send(DNS_WRITES.renew(name)).then(() => {});
const addAlias = (server, label) => send(DNS_WRITES.addAlias(), { server, label });
const removeAlias = (name) => send(DNS_WRITES.removeAlias(name)).then(() => {});

export { DNS_WRITES, addAlias, checkZoneNow, dnsEvents, dnsStatus, originOf as dnsOrigin, removeAlias, renewCertificate };
